import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConversationLink } from "@/components/molecules/ConversationLink";
import { cn } from "@/lib/utils";
import { HeuristicJob } from "@/schemas/firestore/jobs.schema";
import { XIcon } from "lucide-react";

interface CROExpandedCardProps {
  isOpen?: boolean;
  onClose?: () => void;
  // The job, not just its recommendation: the provenance stamp that links back
  // to the conversation lives on the job document, not on the analysis payload.
  job: HeuristicJob;
}

export function CROExpandedCard({
  isOpen = false,
  onClose = () => {},
  job,
}: CROExpandedCardProps) {
  const data = job?.results?.ai_recommendation?.valid_json;

  if (!data) {
    // Optionally, render a loading state or null if data is not yet available
    // This check is important if data might be undefined initially
    return null;
  }

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <SheetContent
        side="bottom"
        className="h-[90dvh] sm:max-w-full overflow-y-auto p-0"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <SheetHeader className="sticky top-0 bg-white z-10 p-4 border-b">
            <div className="flex justify-between items-center">
              <SheetTitle className="text-xl">{data.title}</SheetTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={onClose}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge variant="outline">{data.focus}</Badge>
              <ConversationLink entity={job} />
            </div>
          </SheetHeader>

          {/* Content */}
          <div className="p-6 space-y-8">
            {/* Hypothesis */}
            <div>
              <h3 className="text-lg font-medium mb-2">Hypothesis</h3>
              <p className="text-base">{data.hypothesis}</p>
            </div>

            {/* Justification */}
            <div>
              <h3 className="text-lg font-medium mb-2">Justification</h3>
              <p className="text-base">{data.justification}</p>
            </div>

            {/* Score Breakdown */}
            <div>
              <h3 className="text-lg font-medium mb-4">Score Breakdown</h3>
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-green-100 text-green-800 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold">
                  {data.score.total}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Score: {data.score.total}
                  </p>
                  <p className="text-sm font-medium">
                    Priority: {data.score.priority_level}
                  </p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Impact Row */}
                  <TableRow>
                    <TableCell className="font-medium">Impact</TableCell>
                    <TableCell>{data.score.breakdown.impact.score}</TableCell>
                    <TableCell>
                      <Table>
                        <TableBody>
                          {data.score.breakdown.impact.details.map(
                            (detail, index) => (
                              <TableRow key={index}>
                                <TableCell className="py-1 text-xs">
                                  {detail.name}
                                </TableCell>
                                <TableCell className="py-1 text-xs text-right">
                                  {detail.score}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>

                  {/* Confidence Row */}
                  <TableRow>
                    <TableCell className="font-medium">Confidence</TableCell>
                    <TableCell>
                      {data.score.breakdown.confidence.score}
                    </TableCell>
                    <TableCell>
                      <Table>
                        <TableBody>
                          {data.score.breakdown.confidence.details.map(
                            (detail, index) => (
                              <TableRow key={index}>
                                <TableCell className="py-1 text-xs">
                                  {detail.name}
                                </TableCell>
                                <TableCell className="py-1 text-xs text-right">
                                  {detail.score}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>

                  {/* Effort Row */}
                  <TableRow>
                    <TableCell className="font-medium">Effort</TableCell>
                    <TableCell>{data.score.breakdown.effort.score}</TableCell>
                    <TableCell>
                      <Table>
                        <TableBody>
                          {data.score.breakdown.effort.details.map(
                            (detail, index) => (
                              <TableRow key={index}>
                                <TableCell className="py-1 text-xs">
                                  {detail.name}
                                </TableCell>
                                <TableCell className="py-1 text-xs text-right">
                                  {detail.score}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>

                  {/* Total Row */}
                  <TableRow className="bg-muted/50">
                    <TableCell className="font-medium">Total</TableCell>
                    <TableCell className="font-medium">
                      {data.score.total}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Personas Impacted */}
            <div>
              <h3 className="text-lg font-medium mb-2">Personas Impacted</h3>
              <div className="space-y-3">
                {data.personas_impacted.map((persona, index) => (
                  <div key={index} className="p-3 border rounded-md">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-sm">{persona.name}</h4>
                      <Badge
                        variant={
                          persona.impact.toLowerCase() === "high"
                            ? "destructive"
                            : "secondary"
                        }
                        className={cn(
                          "text-xs py-0 px-1.5",
                          persona.impact.toLowerCase() === "high" &&
                            "bg-red-100 text-red-800",
                          persona.impact.toLowerCase() === "medium" &&
                            "bg-amber-100 text-amber-800",
                          persona.impact.toLowerCase() === "low" &&
                            "bg-green-100 text-green-800",
                        )}
                      >
                        {persona.impact} Impact
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {persona.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Implementation Suggestion */}
            <div>
              <h3 className="text-lg font-medium mb-2">
                {data.implementation_suggestion.title}
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {data.implementation_suggestion.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ul>
            </div>

            {/* Potential Risks */}
            {data.potential_risks && data.potential_risks.length > 0 && (
              <div>
                <h3 className="text-lg font-medium mb-2">Potential Risks</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {data.potential_risks.map((risk, index) => (
                    <li key={index}>{risk}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Google Analytics Evidence */}
            {data.google_analytics_evidence &&
              data.google_analytics_evidence.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-2">
                    Google Analytics Evidence
                  </h3>
                  <Accordion type="single" collapsible className="w-full">
                    {data.google_analytics_evidence.map((evidence, index) => (
                      <AccordionItem key={index} value={`ga-item-${index}`}>
                        <AccordionTrigger className="text-sm">
                          {evidence.title}
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="text-xs">{evidence.insight}</p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
